// src/contexts/TeachersApprovalContext.jsx
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
} from "react";

export const TeachersApprovalContext = createContext(null);

// ✅ Fixed API base (same style as DocumentsContext)
const API_BASE = "https://www.doroosonline.co.il";

/* ===================== Auth headers (COPY from DocumentsContext) ===================== */
const getAuthHeaders = () => {
  const token =
    (typeof window !== "undefined" && (window.__userToken || "")) ||
    localStorage.getItem("token") ||
    localStorage.getItem("jwt") ||
    localStorage.getItem("authToken") ||
    "";

  const headers = { "Content-Type": "application/json" };
  if (token) headers.Authorization = `Bearer ${token}`;
  return headers;
};

const parseJsonSafe = async (res) => {
  const text = await res.text();
  try {
    return { text, data: JSON.parse(text) };
  } catch {
    return { text, data: null };
  }
};

/* ===================== Normalizers ===================== */
const normalizeDocStatus = (s) => {
  const v = String(s || "").toUpperCase();
  const allowed = ["NO_RESPONSE", "NEEDS_REVISION", "APPROVED", "REJECTED"];
  return allowed.includes(v) ? v : "NO_RESPONSE";
};

const normalizeTeacherStatus = (s) => {
  const v = String(s || "").toUpperCase();
  // ✅ you said you want BLOCKED also
  const allowed = ["PENDING", "APPROVED", "REJECTED", "BLOCKED"];
  return allowed.includes(v) ? v : "PENDING";
};

const normalizeSupplierStatus = (s) => {
  const v = String(s || "").toUpperCase();
  const allowed = ["PENDING", "APPROVED", "SUSPENDED"];
  return allowed.includes(v) ? v : "PENDING";
};

const normalizeTeacher = (t) => {
  const documents = Array.isArray(t?.documents) ? t.documents : [];
  return {
    id: Number(t?.id || 0) || t?.id,
    role: t?.role || "teacher",
    firstName: t?.firstName || t?.first_name || "",
    lastName: t?.lastName || t?.last_name || "",
    email: t?.email || "",
    phone: t?.phone || "",

    emailConfirmed: !!(t?.emailConfirmed ?? t?.e_confirmed ?? t?.email_confirmed),
    phoneConfirmed: !!(t?.phoneConfirmed ?? t?.m_confirmed ?? t?.phone_confirmed),

    teacherReviewStarted: !!(t?.teacherReviewStarted ?? t?.teacher_review_started),
    teacherStatus: normalizeTeacherStatus(t?.teacherStatus ?? t?.teacher_status),
    adminNotes: t?.adminNotes ?? t?.teacher_admin_notes ?? "",

    // Supplier-only fields — null/undefined for a teacher row, and
    // that's fine, the UI only reads these when role === 'supplier'.
    supplierName: t?.supplierName ?? t?.supplier_name ?? null,
    businessNumber: t?.businessNumber ?? t?.business_number ?? null,
    gefenSupplierNumber: t?.gefenSupplierNumber ?? t?.gefen_supplier_number ?? null,
    contactPerson: t?.contactPerson ?? t?.contact_person ?? null,
    supplierPhone: t?.supplierPhone ?? t?.supplier_phone ?? null,
    address: t?.address ?? null,
    supplierStatus: t?.supplierStatus != null ? normalizeSupplierStatus(t.supplierStatus) : null,

    documents: documents.map((d) => ({
      id: d?.id,
      type: d?.type || "מסמך",
      fileName: d?.fileName || d?.file_name || "",
      customName: d?.customName || d?.custom_name || "",
      status: normalizeDocStatus(d?.status),
      date: d?.date || d?.created_at || "",
      url: d?.url || "",
      notes: d?.notes || "",
      hidden: !!(d?.hidden), // ✅ חדש
    })),

  };
};

const normalizeCounts = (data) => ({
  PENDING: Number(data?.PENDING ?? 0),
  APPROVED: Number(data?.APPROVED ?? 0),
  REJECTED: Number(data?.REJECTED ?? 0),
  BLOCKED: Number(data?.BLOCKED ?? 0),
  TOTAL: Number(data?.TOTAL ?? 0),
});

/* ===================== Provider ===================== */
export const TeachersApprovalProvider = ({ children }) => {
  const [teachers, setTeachers] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  // filters
  const [filters, setFilters] = useState({
    search: "",
    status: "PENDING", // PENDING|APPROVED|REJECTED|BLOCKED|''
  });

  // pagination
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(6);
  const [total, setTotal] = useState(0);

  // counts
  const [counts, setCounts] = useState({
    PENDING: 0,
    APPROVED: 0,
    REJECTED: 0,
    BLOCKED: 0,
    TOTAL: 0,
  });

  /* ===================== Load Teachers ===================== */
  const loadTeachers = useCallback(async (f = filters, p = page, l = limit) => {
    setIsLoading(true);
    try {
      const q = new URLSearchParams();
      const search = String(f?.search || "").trim();
      const status = String(f?.status || "").trim();

      if (search) q.set("search", search);
      if (status) q.set("status", status);

      q.set("page", String(p || 1));
      q.set("limit", String(l || 6));

      const res = await fetch(`${API_BASE}/api/admin/teachers?${q.toString()}`, {
        method: "GET",
        headers: getAuthHeaders(),
      });

      const { text, data } = await parseJsonSafe(res);

      if (!res.ok || !data?.ok) {
        const msg = data?.error || data?.message || text || res.statusText || "טעינת מורים נכשלה";
        throw new Error(msg);
      }

      const items = Array.isArray(data?.items) ? data.items : [];
      setTeachers(items.map(normalizeTeacher));
      setTotal(Number(data?.total ?? items.length ?? 0));
    } finally {
      setIsLoading(false);
    }
  }, [filters, page, limit]);

  /* ===================== Load Counts ===================== */
  const loadTeachersCounts = useCallback(async (f = filters) => {
    const q = new URLSearchParams();
    const search = String(f?.search || "").trim();
    if (search) q.set("search", search);

    const res = await fetch(`${API_BASE}/api/admin/teachers/counts?${q.toString()}`, {
      method: "GET",
      headers: getAuthHeaders(),
    });

    const { text, data } = await parseJsonSafe(res);

    if (!res.ok || !data?.ok) {
      const msg = data?.error || data?.message || text || res.statusText || "טעינת ספירות מורים נכשלה";
      throw new Error(msg);
    }

    setCounts(normalizeCounts(data));
  }, [filters]);

  /* ===================== Decide Teacher ===================== */
  const adminDecideTeacher = useCallback(async (teacherId, payload) => {
    const id = Number(teacherId || 0);
    if (!id) throw new Error("Missing teacherId");

    const status = String(payload?.status || "").toUpperCase();
    if (!["PENDING", "APPROVED", "REJECTED", "BLOCKED"].includes(status)) {
      throw new Error("Invalid teacher decision status");
    }

    const body = {
      status,
      notes: String(payload?.notes || ""),
    };

    const res = await fetch(`${API_BASE}/api/admin/teachers/${id}/decision`, {
      method: "POST",
      headers: getAuthHeaders(),
      body: JSON.stringify(body),
    });

    const { text, data } = await parseJsonSafe(res);

    if (!res.ok || !data?.ok) {
      const msg = data?.error || data?.message || text || res.statusText || "עדכון מורה נכשל";
      throw new Error(msg);
    }

    return data;
  }, []);

  /* ===================== Initial load ===================== */
  useEffect(() => {
    loadTeachersCounts().catch(() => {});
    loadTeachers(filters, page, limit).catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const value = useMemo(
    () => ({
      teachers,
      isLoading,

      filters,
      setFilters,

      page,
      setPage,
      limit,
      setLimit,
      total,

      counts,
      loadTeachersCounts,

      loadTeachers,
      adminDecideTeacher,
    }),
    [
      teachers,
      isLoading,
      filters,
      page,
      limit,
      total,
      counts,
      loadTeachersCounts,
      loadTeachers,
      adminDecideTeacher,
    ]
  );

  return (
    <TeachersApprovalContext.Provider value={value}>
      {children}
    </TeachersApprovalContext.Provider>
  );
};

export const useTeachersApproval = () => {
  const ctx = useContext(TeachersApprovalContext);
  if (!ctx) throw new Error("useTeachersApproval must be used within TeachersApprovalProvider");
  return ctx;
};